/* eslint-disable no-nested-ternary */
import React from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Button,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
  Alert,
  Box,
} from '@mui/material';

import Today from '@mui/icons-material/Today';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ChevronLeft from '@mui/icons-material/ChevronLeft';

import { NotificationsActive } from '@mui/icons-material';
import { primary } from '../../../../../../utils/brandColors';
import theme from '../../../../../../utils/theme';

const ToolbarComponent = ({
  fullScreen,
  selectedDay,
  setSelectedDay,
  handleRefresh,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  boardSuggestions,
  navigate,
}) => {
  const mediumDown = useMediaQuery(theme.breakpoints.down('md'));
  const largeDown = useMediaQuery(theme.breakpoints.down('lg'));
  // Redux
  const realTimeUpdateError = useSelector((state) => state.board.realTimeUpdateError);
  const numberOfDueDates = Object.keys(boardSuggestions).map((key) => boardSuggestions[key].dueDates || []).filter((dd) => dd.length > 0).length;
  return (
    <Grid
      container
      direction="row"
      justifyContent="flex-start"
      alignItems="center"
    >
      <Grid
        item
        xs={5}
        md={3}
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {
          largeDown
            ? (
              <Tooltip title="Go to Today">
                <IconButton
                  onClick={() => {
                    const date = moment().startOf('day').toDate();
                    if (selectedDay) {
                      // if we were in day view stay in day view
                      setSelectedDay(`${date.getTime()}`);
                    }
                    setStartDate(
                      moment(date).startOf('week').startOf('day').toDate(),
                    );
                    setEndDate(
                      moment(date).endOf('week').endOf('day').toDate(),
                    );
                  }}
                ><Today />
                </IconButton>
              </Tooltip>
            )
            : (
              <Button
                variant="outlined"
                startIcon={<Today />}
                sx={{
                  mr: 2,
                  mb: fullScreen
                    ? 0.5
                    : 0,
                }}
                onClick={() => {
                  const date = moment().startOf('day').toDate();
                  if (selectedDay) {
                    // if we were in day view stay in day view
                    setSelectedDay(`${date.getTime()}`);
                  }
                  setStartDate(
                    moment(date).startOf('week').startOf('day').toDate(),
                  );
                  setEndDate(
                    moment(date).endOf('week').endOf('day').toDate(),
                  );
                }}
              >Today
              </Button>
            )
        }
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Tooltip title="Next">
            <IconButton
              onClick={() => {
                if (selectedDay) {
                  const date = moment(parseInt(selectedDay, 10)).add(-1, 'days').startOf('day').toDate();
                  setStartDate(
                    moment(date).startOf('week').startOf('day').toDate(),
                  );
                  setEndDate(
                    moment(date).endOf('week').endOf('day').toDate(),
                  );
                  setSelectedDay(`${date.getTime()}`);
                } else {
                  setStartDate(
                    moment(startDate).add(-1, selectedDay
                      ? 'days'
                      : 'weeks').startOf('week')
                      .startOf('day')
                      .toDate(),
                  );
                  setEndDate(
                    moment(startDate).add(-1, selectedDay
                      ? 'days'
                      : 'weeks').endOf('week')
                      .endOf('day')
                      .toDate(),
                  );
                }
              }}
              size="small"
              sx={{ backgroundColor: primary.main }}
            ><ChevronLeft sx={{ color: '#ffffff' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Previous">
            <IconButton
              size="small"
              variant="contained"
              sx={{ backgroundColor: primary.main }}
              onClick={() => {
                if (selectedDay) {
                  const date = moment(parseInt(selectedDay, 10)).add(1, 'days').startOf('day').toDate();
                  setStartDate(
                    moment(date).startOf('week').startOf('day').toDate(),
                  );
                  setEndDate(
                    moment(date).endOf('week').endOf('day').toDate(),
                  );
                  setSelectedDay(`${date.getTime()}`);
                } else {
                  setStartDate(
                    moment(startDate).add(1, selectedDay
                      ? 'days'
                      : 'weeks').startOf('week')
                      .startOf('day')
                      .toDate(),
                  );
                  setEndDate(
                    moment(startDate).add(1, selectedDay
                      ? 'days'
                      : 'weeks').endOf('week')
                      .endOf('day')
                      .toDate(),
                  );
                }
              }}
            ><ChevronRight sx={{ color: '#ffffff' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Grid>
      {
        !mediumDown
          ? (
            <Grid item xs={6} align="center">
              <Typography variant="h6" color="primary" align="center">{
                selectedDay
                  ? moment(parseInt(selectedDay, 10)).format('ll')
                  : `${moment(startDate).format('ll')} - ${moment(endDate).format('ll')}`
              }
              </Typography>
              {
                !!realTimeUpdateError
                && (
                  <Grid item xs={12}>
                    <Alert
                      variant="filled"
                      severity="error"
                      sx={{ mb: 2 }}
                      action={(
                        <Button onClick={handleRefresh} color="inherit" size="small">
                          Refresh Now
                        </Button>
                      )}
                    >
                      {realTimeUpdateError || 'There was an error with the board connection - data will be wrong'}
                    </Alert>
                  </Grid>
                )
              }
            </Grid>
          )
          : (
            <Grid item xs={2} md={6} />
          )
      }
      <Grid
        item
        xs={5}
        md={3}
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'right',
        }}
      >
        <Grid
          container
          spacing={1}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          {(numberOfDueDates && navigate && !mediumDown)
            ? (
              <Grid item>
                <Tooltip title={`You have ${numberOfDueDates} horses with reminders at this location. Click to view all reminder information.`}>

                  <IconButton size="small" onClick={() => { navigate('/tasks/reminders'); }}><NotificationsActive color="error" /></IconButton>
                </Tooltip>
              </Grid>
            )
            : <Grid item />}
          <Grid item>
            <ToggleButtonGroup
              color="primary"
              value={selectedDay
                ? 'day'
                : 'week'}
              exclusive
              onChange={(e, newView) => {
                if (newView && newView === 'week' && selectedDay) {
                  setSelectedDay(null);
                } else if (newView && newView === 'day' && !selectedDay) {
                  setSelectedDay(`${moment().startOf('day').toDate().getTime()}`);
                }
              }}
              aria-label="Calendar View"
            >
              <ToggleButton size="small" value="day">Day</ToggleButton>
              <ToggleButton size="small" value="week">Week</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

ToolbarComponent.propTypes = {
  fullScreen: PropTypes.bool,
  selectedDay: PropTypes.any,
  setSelectedDay: PropTypes.func.isRequired,
  handleRefresh: PropTypes.func.isRequired,
  setStartDate: PropTypes.func.isRequired,
  setEndDate: PropTypes.func.isRequired,
  startDate: PropTypes.any.isRequired,
  endDate: PropTypes.any.isRequired,
  boardSuggestions: PropTypes.object.isRequired,
  navigate: PropTypes.func,
};
ToolbarComponent.defaultProps = {
  fullScreen: false,
  selectedDay: null,
  navigate: null,
};

export default (ToolbarComponent);
